import {
  Component, EventEmitter, Injector, Input, OnInit, Output
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { AppComponentBase } from '@shared/app-component-base';
import { finalize } from 'rxjs/operators';
import * as moment from 'moment';
import {
  AgendaDto,
  AgendaProfissionalDto,
  AlterarProfissionalEventoAgendaInputDto,
  CriarEventoAgendaInputDto,
  EventoAgendaDto,
  EventoServiceProxy,
  ExcluirDiaAgendaInputDto,
  ExcluirEventoAgendaInputDto
} from '@shared/service-proxies/service-proxies';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-gerenciar-disponibilidade-dia',
  templateUrl: './gerenciar-disponibilidade-dia.component.html',
  styleUrls: ['./gerenciar-disponibilidade-dia.component.scss']
})
export class GerenciarDisponibilidadeDiaComponent extends AppComponentBase implements OnInit {
  @Input() data: Date;
  @Input() agenda: AgendaDto;
  @Output() onAlterado = new EventEmitter<void>();

  eventos: EventoAgendaDto[] = [];
  carregando = false;
  salvando = false;
  adicionando = false;

  profissionaisDaAgenda: AgendaProfissionalDto[] = [];

  // Formulário de novo horário
  novoInicio = '';
  novoFim = '';
  novoEhRecorrente = false;
  novaDataFimRecorrencia = '';

  // Seletor de profissional — novo horário
  nomeFiltroNovo = '';
  profissionaisFiltradosNovo: AgendaProfissionalDto[] = [];
  profissionalSelecionadoNovo: AgendaProfissionalDto | null = null;

  // Edição inline de profissional por evento
  eventoEditandoId: string | null = null;
  nomeFiltroEdicao = '';
  profissionaisFiltradosEdicao: AgendaProfissionalDto[] = [];
  profissionalSelecionadoEdicao: AgendaProfissionalDto | null = null;

  constructor(
    injector: Injector,
    public bsModalRef: BsModalRef,
    private _eventoService: EventoServiceProxy,
    private _modalService: BsModalService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.profissionaisDaAgenda = this.agenda?.profissionais ?? [];
    if (this.appSession.profissionalId) {
      this.profissionalSelecionadoNovo = this.profissionaisDaAgenda.find(
        ap => ap.profissionalSaudeId === this.appSession.profissionalId
      ) ?? null;
    }
    this.carregarEventos();
  }

  get dataFormatada(): string {
    return moment(this.data).format('DD/MM/YYYY (dddd)');
  }

  carregarEventos(): void {
    this.carregando = true;
    this._eventoService
      .obterEventosDoDia(
        this.agenda.id,
        this.appSession.profissionalId ?? null,
        moment(this.data)
      )
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe((lista) => {
        this.eventos = lista.sort(
          (a, b) => new Date(a.inicio as any).getTime() - new Date(b.inicio as any).getTime()
        );
      });
  }

  cancelarAdicao(): void {
    this.adicionando = false;
    this.limparFormulario();
  }

  // ── Seletor de profissional: formulário de novo horário ──

  filtrarProfissionaisNovo(nome: string): void {
    if (!nome) {
      this.profissionaisFiltradosNovo = [];
      return;
    }
    this.profissionaisFiltradosNovo = this.profissionaisDaAgenda
      .filter(ap => ap.profissionalSaude?.nome?.toLowerCase().includes(nome.toLowerCase()));
  }

  selecionarProfissionalNovo(ap: AgendaProfissionalDto): void {
    this.profissionalSelecionadoNovo = ap;
    this.profissionaisFiltradosNovo = [];
    this.nomeFiltroNovo = '';
  }

  removerProfissionalNovo(): void {
    this.profissionalSelecionadoNovo = null;
  }

  // ── Seletor de profissional: edição inline por evento ──

  iniciarEdicaoProfissional(evento: EventoAgendaDto): void {
    this.eventoEditandoId = evento.id;
    this.profissionalSelecionadoEdicao = this.profissionaisDaAgenda.find(
      ap => ap.profissionalSaudeId === evento.profissionalId
    ) ?? null;
    this.nomeFiltroEdicao = '';
    this.profissionaisFiltradosEdicao = [];
  }

  cancelarEdicaoProfissional(): void {
    this.eventoEditandoId = null;
    this.profissionalSelecionadoEdicao = null;
    this.nomeFiltroEdicao = '';
    this.profissionaisFiltradosEdicao = [];
  }

  filtrarProfissionaisEdicao(nome: string): void {
    if (!nome) {
      this.profissionaisFiltradosEdicao = [];
      return;
    }
    this.profissionaisFiltradosEdicao = this.profissionaisDaAgenda
      .filter(ap => ap.profissionalSaude?.nome?.toLowerCase().includes(nome.toLowerCase()));
  }

  selecionarProfissionalEdicao(ap: AgendaProfissionalDto): void {
    this.profissionalSelecionadoEdicao = ap;
    this.profissionaisFiltradosEdicao = [];
    this.nomeFiltroEdicao = '';
  }

  removerProfissionalEdicao(): void {
    this.profissionalSelecionadoEdicao = null;
  }

  salvarAlteracaoProfissional(evento: EventoAgendaDto): void {
    if (!this.profissionalSelecionadoEdicao) {
      this.message.warn('Selecione um profissional.');
      return;
    }

    if (!evento.ehRecorrente) {
      this.alterarProfissional(evento, this.profissionalSelecionadoEdicao.profissionalSaudeId);
      return;
    }

    Swal.fire({
      title: 'Alterar profissional',
      text: 'A alteração será aplicada em todas as recorrências deste horário. Confirmar?',
      icon: 'question',
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Confirmar',
    }).then(result => {
      if (result.isConfirmed) {
        this.alterarProfissional(evento, this.profissionalSelecionadoEdicao!.profissionalSaudeId);
      }
    });
  }

  adicionarHorario(): void {
    if (this.profissionaisDaAgenda.length > 0 && !this.profissionalSelecionadoNovo) {
      this.message.warn('Selecione o profissional responsável pelo horário.');
      return;
    }

    if (!this.novoInicio || !this.novoFim) {
      this.message.warn('Preencha o horário de início e fim.');
      return;
    }

    const dataStr = moment(this.data).format('YYYY-MM-DD');
    const inicio = moment.utc(`${dataStr} ${this.novoInicio}`, 'YYYY-MM-DD HH:mm');
    const fim = moment.utc(`${dataStr} ${this.novoFim}`, 'YYYY-MM-DD HH:mm');

    if (!fim.isAfter(inicio)) {
      this.message.warn('O horário inicial deve ser menor que o horário final.');
      return;
    }

    const input = new CriarEventoAgendaInputDto();
    input.agendaId = this.agenda.id;
    input.profissionalId = this.profissionalSelecionadoNovo?.profissionalSaudeId ?? null;
    input.clinicaId = this.appSession.clinicaId ?? null;
    input.inicio = inicio;
    input.fim = fim;
    input.ehRecorrente = this.novoEhRecorrente;
    input.dataFimRecorrencia = this.novoEhRecorrente && this.novaDataFimRecorrencia
      ? moment(this.novaDataFimRecorrencia)
      : null;

    this.salvando = true;
    abp.ui.setBusy();

    this._eventoService
      .criarEventoAgenda(input)
      .pipe(finalize(() => { abp.ui.clearBusy(); this.salvando = false; }))
      .subscribe(() => {
        abp.notify.success('Horário criado com sucesso.');
        this.adicionando = false;
        this.limparFormulario();
        this.carregarEventos();
        this.onAlterado.emit();
      });
  }

  confirmarExclusaoEvento(evento: EventoAgendaDto): void {
    if (!evento.ehRecorrente) {
      Swal.fire({
        title: 'Excluir horário',
        text: 'Deseja excluir este horário?',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Excluir',
      }).then(result => {
        if (result.isConfirmed) this.excluirEvento(evento, false);
      });
      return;
    }

    Swal.fire({
      title: 'Excluir horário',
      text: 'Deseja excluir somente este horário ou este e todas as próximas recorrências?',
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      cancelButtonText: 'Cancelar',
      denyButtonText: 'Este e recorrências',
      confirmButtonText: 'Somente este',
      customClass: {
        confirmButton: 'swal-btn-perigo',
        denyButton: 'swal-btn-neutro',
        cancelButton: 'swal-btn-cancelar'
      }
    }).then(result => {
      if (result.isConfirmed) this.excluirEvento(evento, false);
      if (result.isDenied) this.excluirEvento(evento, true);
    });
  }

  confirmarExclusaoDia(): void {
    Swal.fire({
      title: 'Excluir dia inteiro',
      text: `Deseja excluir somente os horários de ${this.dataFormatada} ou também as próximas recorrências deste dia?`,
      icon: 'warning',
      showCancelButton: true,
      showDenyButton: true,
      cancelButtonText: 'Cancelar',
      denyButtonText: 'Este e recorrências',
      confirmButtonText: 'Somente este',
      customClass: {
        confirmButton: 'swal-btn-perigo',
        denyButton: 'swal-btn-neutro',
        cancelButton: 'swal-btn-cancelar'
      }
    }).then(result => {
      if (result.isConfirmed) this.excluirDia(false);
      if (result.isDenied) this.excluirDia(true);
    });
  }

  nomeProfissional(profissionalId: string | undefined): string {
    if (!profissionalId) return 'Sem profissional';
    const ap = this.profissionaisDaAgenda.find(p => p.profissionalSaudeId === profissionalId);
    return ap?.profissionalSaude?.nome ?? 'Profissional';
  }

  fechar(): void {
    this.bsModalRef.hide();
  }

  private alterarProfissional(evento: EventoAgendaDto, profissionalId: string): void {
    const input = new AlterarProfissionalEventoAgendaInputDto();
    input.eventoId = evento.id;
    input.profissionalId = profissionalId;

    this.salvando = true;
    abp.ui.setBusy();

    this._eventoService
      .alterarProfissionalEventoAgenda(input)
      .pipe(finalize(() => { abp.ui.clearBusy(); this.salvando = false; }))
      .subscribe(() => {
        abp.notify.success('Profissional alterado com sucesso.');
        this.cancelarEdicaoProfissional();
        this.carregarEventos();
        this.onAlterado.emit();
      });
  }

  private excluirEvento(evento: EventoAgendaDto, excluirRecorrentes: boolean): void {
    const input = new ExcluirEventoAgendaInputDto();
    input.eventoId = evento.id;
    input.excluirRecorrentes = excluirRecorrentes;
    input.dataReferencia = excluirRecorrentes ? moment(this.data) : null;

    this.salvando = true;
    abp.ui.setBusy();

    this._eventoService
      .excluirEventoAgenda(input)
      .pipe(finalize(() => { abp.ui.clearBusy(); this.salvando = false; }))
      .subscribe(() => {
        abp.notify.success('Horário removido com sucesso.');
        this.carregarEventos();
        this.onAlterado.emit();
      });
  }

  private excluirDia(excluirProximos: boolean): void {
    const input = new ExcluirDiaAgendaInputDto();
    input.agendaId = this.agenda.id;
    input.profissionalId = this.appSession.profissionalId ?? null;
    input.data = moment(this.data);
    input.excluirProximosEquivalentes = excluirProximos;

    this.salvando = true;
    abp.ui.setBusy();

    this._eventoService
      .excluirDiaAgenda(input)
      .pipe(finalize(() => { abp.ui.clearBusy(); this.salvando = false; }))
      .subscribe(() => {
        abp.notify.success('Horários removidos com sucesso.');
        this.carregarEventos();
        this.onAlterado.emit();
      });
  }

  private limparFormulario(): void {
    this.novoInicio = '';
    this.novoFim = '';
    this.novoEhRecorrente = false;
    this.novaDataFimRecorrencia = '';
    this.nomeFiltroNovo = '';
    this.profissionaisFiltradosNovo = [];
    this.profissionalSelecionadoNovo = this.profissionaisDaAgenda.find(
      ap => ap.profissionalSaudeId === this.appSession.profissionalId
    ) ?? null;
  }
}
